import { createApi, createCompanyApi, createHttp, type ApiError, type LoginResponse, type Session, type SessionStore } from '@vlm/api';
import { env } from '../env';

export const SESSION_KEY = 'vlm.admin.session';

let memory: Session | null = null;

/**
 * The tokens live in localStorage, so every tab shares one sign-in (the old admin kept them per
 * tab). If storage is blocked, the session lasts as long as the page.
 */
export const sessionStore: SessionStore = {
  get() {
    try {
      const value = localStorage.getItem(SESSION_KEY);
      if (value === null) return memory;
      const session = JSON.parse(value) as Partial<Session>;
      return session.accessToken && session.refreshToken ? (session as Session) : null;
    } catch {
      return memory;
    }
  },
  set(session) {
    memory = session;
    try {
      if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
      else localStorage.removeItem(SESSION_KEY);
    } catch {
      // storage blocked: `memory` carries it
    }
  },
};

/**
 * Set by auth.ts: what to do when the refresh token is gone (with the API's reason, when it gave one —
 * the company was suspended), and with the user a refresh brings back.
 */
export const sessionEvents: { expired: (reason: ApiError | null) => void; refreshed: (login: LoginResponse) => void } = {
  expired: () => {},
  refreshed: () => {},
};

export const http = createHttp({
  baseUrl: env.apiUrl,
  session: sessionStore,
  onSessionExpired: (reason) => sessionEvents.expired(reason),
  onRefreshed: (login) => sessionEvents.refreshed(login),
});

export const api = createApi(http);

/** The signed-in user's own company — its plan, billing and invoices (/api/v1/company, docs/SAAS.md §4.2). */
export const companyApi = createCompanyApi(http);

/** A file the API serves (profile photos live under /uploads/…); `version` busts the cache after a change. */
export function fileUrl(path: string | null | undefined, version?: string | null): string | null {
  if (!path) return null;
  const base = /^https?:\/\//.test(path) ? path : `${env.apiUrl}${path.startsWith('/') ? '' : '/'}${path}`;
  return version ? `${base}${base.includes('?') ? '&' : '?'}v=${encodeURIComponent(version)}` : base;
}
